import { useEffect, useRef, useSyncExternalStore } from 'react';

/**
 * Module-level store for touch device state.
 * Shared across all hook instances for consistent behavior.
 * Defaults to false (non-touch) until client-side detection runs.
 */
let touchDeviceState = false;
let initialized = false;
const listeners: Set<() => void> = new Set();

/**
 * Cached touch capability detection.
 * Unlike touchDeviceState (which tracks current input mode), this detects
 * whether the device HAS touch capability at all (useful for hybrid devices).
 */
let touchCapability: boolean | null = null;

/**
 * Reset the module state for testing purposes only.
 * @internal - Do not use in production code.
 */
export function __resetForTesting(): void {
  touchDeviceState = false;
  initialized = false;
  listeners.clear();
  touchCapability = null;
}

/**
 * Detect whether the device has touch capability.
 * Uses `(any-pointer: coarse)` which is true if ANY pointing device is coarse (touch).
 * This is different from `(hover: hover)` which checks primary input.
 * On hybrid devices (Surface, iPad with keyboard), this returns true even if mouse is primary.
 */
function getTouchCapability(): boolean {
  if (touchCapability === null && typeof window !== 'undefined') {
    touchCapability = window.matchMedia('(any-pointer: coarse)').matches;
  }
  return touchCapability ?? false;
}

/**
 * Reinitialize state from matchMedia for testing purposes only.
 * @internal - Do not use in production code.
 */
export function __initializeForTesting(): void {
  if (typeof window !== 'undefined') {
    const canHover = window.matchMedia('(hover: hover)').matches;
    touchDeviceState = !canHover;
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): boolean {
  return touchDeviceState;
}

function getServerSnapshot(): boolean {
  return false; // SSR defaults to non-touch until client-side detection runs
}

function setTouchDeviceState(value: boolean): void {
  if (touchDeviceState !== value) {
    touchDeviceState = value;
    listeners.forEach((listener) => listener());
  }
}

// Initialize state on first client load (runs once, before any component mounts)
if (typeof window !== 'undefined' && !initialized) {
  const canHover = window.matchMedia('(hover: hover)').matches;
  touchDeviceState = !canHover;
  initialized = true;
}

/**
 * Hook to detect if the current device is using touch input.
 *
 * Uses `useSyncExternalStore` for synchronous hydration - eliminates the
 * flash/double-render that occurs with useState + useEffect patterns.
 *
 * Returns:
 * - `isTouchDevice`:
 *   - `true`: Touch device detected (or touch input used on hybrid device)
 *   - `false`: Non-touch device (mouse/trackpad), or SSR (defaults to non-touch)
 * - `hasTouchCapability`:
 *   - `true`: Device HAS touch capability (even if currently using mouse)
 *   - `false`: No touch capability, or SSR
 *
 * On hybrid devices (touchscreen laptops), `isTouchDevice` updates dynamically
 * based on the most recent input type (touch vs mouse), while `hasTouchCapability`
 * remains constant (true if device has touchscreen).
 */
const useTouchDevice = (): { isTouchDevice: boolean; hasTouchCapability: boolean } => {
  const pendingUpdate = useRef<number | null>(null);

  // useSyncExternalStore reads state synchronously during hydration,
  // eliminating the flash that useState + useEffect would cause.
  const isTouchDevice = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  useEffect(() => {
    // Use Pointer Events API for unified input handling on hybrid devices.
    // pointerdown fires only on click/tap (not continuous movement like mousemove)
    const handlePointerDown = (e: PointerEvent) => {
      const isTouchInput = e.pointerType === 'touch';

      // Cancel any pending update
      if (pendingUpdate.current !== null) {
        cancelAnimationFrame(pendingUpdate.current);
      }

      // Defer state update to after the current event cycle completes.
      // This ensures the current tap/click finishes processing on the existing
      // element before any re-render caused by the state change.
      // Fixes the "first tap consumed" issue on hybrid devices when switching
      // between mouse and touch input.
      pendingUpdate.current = requestAnimationFrame(() => {
        setTouchDeviceState(isTouchInput);
        pendingUpdate.current = null;
      });
    };

    window.addEventListener('pointerdown', handlePointerDown, { passive: true });

    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      if (pendingUpdate.current !== null) {
        cancelAnimationFrame(pendingUpdate.current);
      }
    };
  }, []);

  return { isTouchDevice, hasTouchCapability: getTouchCapability() };
};

export default useTouchDevice;
